/**
 * The SMS code for pages on a company's own subdomain (`acme.peopleorbit.in`), sent from the app
 * domain (`app.peopleorbit.in`) through a hidden frame.
 *
 * Firebase only sends a code to a page whose host is on the project's "Authorized domains" list, and
 * that list takes exact hosts — no `*.peopleorbit.in`. Every company has its own subdomain, created at
 * sign-up, so listing them is not an option. The app domain is listed once, so a company page loads
 * `app.<domain>/phone-verify` in an iframe and asks it, by `postMessage`, to send and check the code.
 * The frame hands back the same Firebase ID token the page would have got itself; the API checks it
 * exactly as before (the number in it must be the person's own, and each proof is spent once).
 *
 * Only the browser takes this path. The Android app verifies with the phone's own Firebase SDK, which
 * checks the app's signing key rather than the page's host (`nativePhoneVerifier.ts`).
 *
 * nginx lets only `https://*.<domain>` frame `/phone-verify` (`frame-ancestors`); every other page
 * keeps `X-Frame-Options: SAMEORIGIN`. The frame itself answers only its direct parent, and only when
 * that parent is on this domain.
 */

import { APP_DOMAIN } from './subdomain.js';
import {
  PhoneVerificationError,
  type PhoneCodeSession,
  type PhoneVerificationFailure,
  type PhoneVerifier,
} from './phoneVerification.js';

export const PHONE_VERIFY_PATH = '/phone-verify';

/** Every message either side sends carries this, so unrelated `postMessage` traffic is ignored. */
export const PHONE_VERIFY_TAG = 'hrms-phone-verify';

export type ToFrame =
  | { tag: typeof PHONE_VERIFY_TAG; kind: 'send'; id: number; phone: string }
  | { tag: typeof PHONE_VERIFY_TAG; kind: 'confirm'; id: number; session: number; code: string };

export type FromFrame =
  | { tag: typeof PHONE_VERIFY_TAG; kind: 'ready' }
  | { tag: typeof PHONE_VERIFY_TAG; kind: 'sent'; id: number; session: number }
  | { tag: typeof PHONE_VERIFY_TAG; kind: 'proof'; id: number; proof: string }
  | { tag: typeof PHONE_VERIFY_TAG; kind: 'failed'; id: number; reason: PhoneVerificationFailure }
  /** A reCAPTCHA puzzle opened (or closed) inside the frame: the page shows (or hides) the frame. */
  | { tag: typeof PHONE_VERIFY_TAG; kind: 'challenge'; open: boolean };

/** `Omit` per member of a union (plain `Omit` keeps only the keys every member shares). */
export type Untagged<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never;

export function isTagged<T extends { tag: string }>(data: unknown): data is T {
  return (
    Boolean(data) &&
    typeof data === 'object' &&
    (data as { tag?: unknown }).tag === PHONE_VERIFY_TAG
  );
}

function portSuffix(location: Location): string {
  return location.port ? `:${location.port}` : '';
}

/** `https://app.peopleorbit.in` — the one host Firebase has to know about. */
export function phoneVerifyFrameOrigin(location: Location = window.location): string {
  return `${location.protocol}//app.${APP_DOMAIN}${portSuffix(location)}`;
}

/** True on a company subdomain, where Firebase would refuse the page's own host. */
export function needsPhoneVerifyFrame(location: Location = window.location): boolean {
  const host = location.hostname.toLowerCase();
  return host.endsWith(`.${APP_DOMAIN}`) && location.origin !== phoneVerifyFrameOrigin(location);
}

/** A parent the frame will answer: this domain or one of its subdomains, on the frame's own scheme. */
export function isTrustedParentOrigin(
  origin: string,
  location: Location = window.location,
): boolean {
  let url: URL;
  try {
    url = new URL(origin);
  } catch {
    return false;
  }
  if (url.protocol !== location.protocol) return false;
  const host = url.hostname.toLowerCase();
  return host === APP_DOMAIN || host.endsWith(`.${APP_DOMAIN}`);
}

/** How long each send waits for the frame to load and say it is listening. */
export const FRAME_READY_TIMEOUT_MS = 15_000;

/** A send or confirm that never answers (the frame crashed, the puzzle was dismissed). */
export const FRAME_REPLY_TIMEOUT_MS = 120_000;

// Always full-viewport, so reCAPTCHA lays its puzzle out against the real window size; hidden is
// only invisible and click-through.
const FRAME_STYLE =
  'position:fixed;inset:0;width:100%;height:100%;border:0;z-index:2147483647;background:transparent;';
const HIDDEN_STYLE = `${FRAME_STYLE}opacity:0;pointer-events:none;`;
const OPEN_STYLE = `${FRAME_STYLE}opacity:1;pointer-events:auto;`;

/**
 * The page side: a `PhoneVerifier` whose work happens in the app-domain frame. The frame is attached
 * to `document.body` (not inside the caller's dialog, where a transformed ancestor would trap a
 * `position: fixed` puzzle) and removed on `dispose`.
 *
 * Callers keep one verifier for the life of their dialog, so nothing here fails for good: each send
 * waits for the frame afresh (a load slower than the timeout still counts once it arrives), and a
 * send that never answered replaces the frame, whose own Firebase call may be stuck.
 */
export function createFramedPhoneVerifier(doc: Document = document): PhoneVerifier {
  const view = doc.defaultView ?? window;
  const origin = phoneVerifyFrameOrigin(view.location);
  const pending = new Map<
    number,
    {
      resolve: (value: FromFrame) => void;
      reject: (error: PhoneVerificationError) => void;
      timer: number;
    }
  >();
  let nextId = 1;
  let disposed = false;
  let frame: HTMLIFrameElement;
  let isReady = false;
  let readyWaiters: (() => void)[] = [];

  function mount() {
    frame = doc.createElement('iframe');
    frame.src = `${origin}${PHONE_VERIFY_PATH}`;
    frame.title = 'Mobile number verification';
    frame.setAttribute('aria-hidden', 'true');
    frame.tabIndex = -1;
    show(false);
    isReady = false;
    doc.body.appendChild(frame);
  }

  function show(open: boolean) {
    frame.style.cssText = open ? OPEN_STYLE : HIDDEN_STYLE;
    // Same scheme on both sides, so the browser lets the frame stay transparent over the page.
    frame.style.colorScheme = 'light';
  }

  function waitReady(): Promise<void> {
    if (isReady) return Promise.resolve();
    return new Promise<void>((resolve, reject) => {
      const timer = view.setTimeout(() => {
        readyWaiters = readyWaiters.filter((waiter) => waiter !== done);
        reject(new PhoneVerificationError('unavailable'));
      }, FRAME_READY_TIMEOUT_MS);
      const done = () => {
        view.clearTimeout(timer);
        resolve();
      };
      readyWaiters.push(done);
    });
  }

  function onMessage(event: MessageEvent) {
    if (event.origin !== origin || event.source !== frame.contentWindow) return;
    if (!isTagged<FromFrame>(event.data)) return;
    const message = event.data;
    switch (message.kind) {
      case 'ready': {
        isReady = true;
        const waiters = readyWaiters;
        readyWaiters = [];
        for (const waiter of waiters) waiter();
        return;
      }
      case 'challenge':
        show(message.open);
        return;
      default: {
        const waiter = pending.get(message.id);
        if (!waiter) return;
        pending.delete(message.id);
        view.clearTimeout(waiter.timer);
        if (message.kind === 'failed') waiter.reject(new PhoneVerificationError(message.reason));
        else waiter.resolve(message);
      }
    }
  }

  mount();
  view.addEventListener('message', onMessage);

  async function ask(message: Untagged<ToFrame, 'tag' | 'id'>): Promise<FromFrame> {
    if (disposed) throw new PhoneVerificationError('unknown');
    await waitReady();
    const id = nextId++;
    return new Promise<FromFrame>((resolve, reject) => {
      const timer = view.setTimeout(() => {
        pending.delete(id);
        // The frame's Firebase call may never settle (a dismissed puzzle): start over with a new one.
        if (message.kind === 'send') {
          frame.remove();
          mount();
        } else {
          show(false);
        }
        reject(new PhoneVerificationError('unknown'));
      }, FRAME_REPLY_TIMEOUT_MS);
      pending.set(id, { resolve, reject, timer });
      frame.contentWindow?.postMessage(
        { ...message, tag: PHONE_VERIFY_TAG, id } as ToFrame,
        origin,
      );
    });
  }

  let inFlight: { phone: string; promise: Promise<PhoneCodeSession> } | null = null;

  async function sendFresh(phone: string): Promise<PhoneCodeSession> {
    const reply = await ask({ kind: 'send', phone });
    if (reply.kind !== 'sent') throw new PhoneVerificationError('unknown');
    const session = reply.session;
    return {
      async confirm(code) {
        const answer = await ask({ kind: 'confirm', session, code });
        if (answer.kind !== 'proof') throw new PhoneVerificationError('unknown');
        return answer.proof;
      },
    };
  }

  return {
    send(phone) {
      if (inFlight?.phone === phone) return inFlight.promise;
      const promise = sendFresh(phone);
      const entry = { phone, promise };
      inFlight = entry;
      const done = () => {
        if (inFlight === entry) inFlight = null;
      };
      promise.then(done, done);
      return promise;
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      view.removeEventListener('message', onMessage);
      for (const waiter of pending.values()) {
        view.clearTimeout(waiter.timer);
        waiter.reject(new PhoneVerificationError('unknown'));
      }
      pending.clear();
      frame.remove();
    },
  };
}
